import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { Link } from 'react-router-dom';

interface Donation {
  id: string;
  amount: number;
  currency: string;
  frequency: string;
  status: string;
  message: string | null;
  created_at: string;
}

interface VolunteerApp {
  id: string;
  program: string;
  availability: string;
  status: string;
  created_at: string;
}

export default function Profile() {
  const { user, profile, signOut } = useAuth();
  const [donations, setDonations] = useState<Donation[]>([]);
  const [volunteers, setVolunteers] = useState<VolunteerApp[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    async function loadData() {
      const { data: donationData } = await supabase
        .from('donations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      const { data: volunteerData } = await supabase
        .from('volunteers')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      setDonations(donationData || []);
      setVolunteers(volunteerData || []);
      setLoading(false);
    }

    loadData();
  }, [user]);

  const totalDonated = donations.reduce((sum, d) => sum + Number(d.amount), 0);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-5">
            <i className="ri-user-line text-gray-400 text-2xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Sign in to view your profile</h2>
          <p className="text-gray-500 mb-6">Track your donations and volunteer applications in one place.</p>
          <Link
            to="/login"
            className="inline-block px-8 py-3 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-colors"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-50">
      <section className="w-full pt-28 pb-12 px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Profile Header */}
          <div className="bg-white rounded-2xl p-8 mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-brand-500 flex items-center justify-center text-white text-2xl font-bold">
                  {profile?.full_name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{profile?.full_name || 'Member'}</h1>
                  <p className="text-gray-500">{user.email}</p>
                </div>
              </div>
              <button
                onClick={signOut}
                className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Sign Out
              </button>
            </div>
          </div>

          {/* Impact Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6">
              <p className="text-gray-500 text-sm mb-1">Total Donated</p>
              <p className="text-3xl font-bold text-gray-900">${totalDonated.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl p-6">
              <p className="text-gray-500 text-sm mb-1">Donations Made</p>
              <p className="text-3xl font-bold text-gray-900">{donations.length}</p>
            </div>
            <div className="bg-white rounded-2xl p-6">
              <p className="text-gray-500 text-sm mb-1">Volunteer Apps</p>
              <p className="text-3xl font-bold text-gray-900">{volunteers.length}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Donations History */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Donation History</h2>
              {loading ? (
                <p className="text-gray-400">Loading...</p>
              ) : donations.length === 0 ? (
                <div className="text-center py-8">
                  <i className="ri-heart-3-line text-gray-300 text-4xl mb-3 block"></i>
                  <p className="text-gray-400 mb-4">No donations yet</p>
                  <Link
                    to="/donate"
                    className="text-brand-600 font-semibold hover:text-brand-700"
                  >
                    Make your first donation
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {donations.map((d) => (
                    <div key={d.id} className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-900">${Number(d.amount).toLocaleString()}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(d.created_at).toLocaleDateString()} &middot; {d.frequency === 'monthly' ? 'Monthly' : 'One-time'}
                        </p>
                        {d.message && (
                          <p className="text-xs text-gray-400 italic mt-1">&ldquo;{d.message}&rdquo;</p>
                        )}
                      </div>
                      <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                        d.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {d.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Volunteer Applications */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Volunteer Applications</h2>
              {loading ? (
                <p className="text-gray-400">Loading...</p>
              ) : volunteers.length === 0 ? (
                <div className="text-center py-8">
                  <i className="ri-hand-heart-line text-gray-300 text-4xl mb-3 block"></i>
                  <p className="text-gray-400 mb-4">No applications yet</p>
                  <Link
                    to="/volunteer"
                    className="text-brand-600 font-semibold hover:text-brand-700"
                  >
                    Apply to volunteer
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {volunteers.map((v) => (
                    <div key={v.id} className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-900 capitalize">{v.program} Program</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(v.created_at).toLocaleDateString()} &middot; {v.availability}
                        </p>
                      </div>
                      <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                        v.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : v.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {v.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}